<template>
    <v-card class="d-inline-block mr-4" @dragover.prevent="handleDragOver" @drop.prevent="handleFileDrop">
        <v-toolbar v-if="repeatableChild">
            <v-icon  class="drag-handle" :title="$t('common.actions.move')" size="x-large">mdi-drag-vertical</v-icon>
            <v-card-title>{{ props.label }}</v-card-title>
        </v-toolbar>
        <template v-else>
            <v-card-title>{{ props.label }}</v-card-title>
            <v-card-subtitle v-if="props.hint">{{ props.hint }}</v-card-subtitle>
        </template>

        <v-card-text>
            <template v-if="fileUrl" >
                <v-icon class="no-decoration" size="large">mdi-file-pdf-box</v-icon>
                <a :href="fileUrl" target="_blank">
                     {{ fileInfo?.original_file_name || value }}
                </a>
                <span class="d-block" v-if="fileInfo?.uploaded_at">{{ $t('editor.fields.pdf.uploaded_at', {date: niceDateTime(fileInfo.uploaded_at)}) }}</span>
            </template>
            <v-btn v-else @click="toggleFileSelect">
                {{ $t('editor.fields.pdf.empty') }}
            </v-btn>
        </v-card-text>

        <v-card-actions>
            <input type="file" ref="uploader" style="display: none" @change="handleFileSelect">
            <v-icon @click="toggleFileSelect" class="ml-2"
                    :title="$t('editor.fields.upload.max_size', {size: humanFileSize(max_upload_size, true)})">
                mdi-tray-arrow-up
            </v-icon>
            <v-spacer></v-spacer>
            <v-menu>
                <template v-slot:activator="{ props }">
                    <v-btn icon="mdi-dots-vertical" size="x-small" variant="flat" class="ml-auto" v-bind="props">
                    </v-btn>
                </template>
                <v-list density="compact">
                    <v-list-item @click="handleShowCurrent" :disabled="value === ''">{{ $t('common.actions.preview') }}</v-list-item>
<!--                    <v-list-item @click="handleShowOriginal" :disabled="value === ''">Original ansehen</v-list-item>-->
                    <v-list-item @click="handleDeleteCustomised" :disabled="!isDeletable">
                        <v-list-item-title :title="$t('editor.fields.pdf.restore_hint')">{{ $t('common.actions.delete') }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-card-actions>
        <!-- File Size Upload Warnings. @Todo: Needs restyling? -->
        <ul v-if="warnings.length">
            <li v-for="(warning, index) in warnings" :key="index">
                {{ warning }} <v-btn @click="warnings.splice(index,1)" icon="mdi-close"></v-btn>
            </li>
        </ul>
    </v-card>
</template>

<script setup>
import {computed, watch, ref, onMounted} from "vue";
import {humanFileSize, niceDateTime} from "../../utils/helpers";
import * as api from "../../services/api";
// https://www.npmjs.com/package/jsonpath
import jp from "jsonpath";
import {getStoreByName} from "../../utils/storeHelpers";
import {mandatory_props, resolveFieldPath} from "../../utils/editorConfigFieldHelpers";
import {persistEditorFieldChange} from "../../utils/editorFieldPersistence.mjs";
import {useI18n} from "@/i18n";

const {t} = useI18n();

defineExpose({toggleFileSelect});

const max_upload_size = 500000; // 500 KB
const warnings = ref([]);
const is_uploading = ref(false);
const uploader = ref(null);
const upload_progress = ref(0);
const fileInfo = ref({});

const base_url = import.meta.env.VITE_DELIVERY_SYSTEM_URL;
const props = defineProps({
    ...mandatory_props,
    repeatableChild: {
        type: Boolean,
        default: false,
    }
});
const store = getStoreByName(props.storeName);

/**
 * The effective json_path of this field.
 * @see resolveFieldPath() - handles fields inside (repeatable) SubPanels
 */
const jsonPath = computed(() => resolveFieldPath(store, props.json_path));
const value = computed({
    get() {
        return jp.value(store[props.jsonKey], jsonPath.value);
    },
    set(v) {
        jp.value(store[props.jsonKey], jsonPath.value, v);
    }
});

const fileUrl = computed(() => {
    if (value.value) {
        if (value.value === '#') return null;
        if (value.value.startsWith('http')) return value.value;
        return `${base_url}${value.value}`;
    }
    return null;
});

const emits = defineEmits(['deleted']);

// ----------------------------------------------------------------------------
// Show Assets
// ----------------------------------------------------------------------------

function handleImageClick() {
    // Click on placeholder image
    if (value.value === '') return toggleFileSelect();
    // click on "real" image
    handleShowCurrent();
}

/**
 * Show the current (customised) asset
 */
function handleShowCurrent() {
    const w = window.open(`${base_url}${value.value}`, '_blank');
    w.focus();
}

/**
 * Show the original asset
 */
function handleShowOriginal() {
    const url = window.route('assets.getOriginal', {
        step: store.uuid,
        json_path: jsonPath.value
    });

    api.client.get(url).then(response => {
        const w = window.open(`${base_url}${response.data.original_path}`, '_blank');
        w.focus();
    });
}

// ----------------------------------------------------------------------------
// File Upload
// ----------------------------------------------------------------------------
function toggleFileSelect() {
    uploader.value.click();
}
// Do not delete this function, it is needed for the drag and drop function!
function handleDragOver(event) { }
function handleFileDrop(event) {
    // Prevent default behavior (Prevent file from being opened)
    event.preventDefault();
    let source = event.dataTransfer.items || event.dataTransfer.files;

    // Use DataTransferItemList interface to access the file(s)
    for (let i = 0; i < source.length; i++) {
        // If dropped items aren't files, reject them
        if (source[i].kind === 'file') {
            let file = source[i].getAsFile();
            if (file.size < max_upload_size) {
                uploadFileForStep(file).then(getFileInfo);
            } else {
                addWarning(file);
            }
        }
    }
}
function handleFileSelect(event) {
    for (let i = 0; i < event.target.files.length; i++) {
        let file = event.target.files[i];

        if (file.size < max_upload_size) {
            uploadFileForStep(file).then(getFileInfo);
        } else {
            addWarning(file);
        }
    }
}
async function uploadFileForStep(file) {
    const url = store.getAssetUploadUrl();
    const form = new FormData();
    form.append('asset', file);

    let config = {
        onUploadProgress: (progressEvent) => {
            upload_progress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
    }

    is_uploading.value = true;

    try {
        const response = await api.client.post(url, form, config);

        // Dateipfad zunächst nur in die lokale Arbeitskopie übernehmen.
        value.value = response.data.path;
        await persistEditorFieldChange(props.storeName, () => store.save());

        return response.data;
    } finally {
        is_uploading.value = false;
    }

}
function addWarning(file) {
    warnings.value.push(t('editor.fields.upload.too_large', {
        name: file.name,
        size: humanFileSize(file.size, true),
        max: humanFileSize(max_upload_size, true),
    }));
}

// ----------------------------------------------------------------------------
// Delete / Restore
// ----------------------------------------------------------------------------

/**
 * Customised Assets are stored in the "/accounts/" folder
 *
 * @type {ComputedRef<*>}
 */
const isDeletable = computed(() => {
    return value.value === "" || value.value.startsWith('/accounts');
});

/**
 * handle delete customised (asset).
 * - Tell the server to delete the customised asset
 * - "We" (the client) then overwrite the current image with the original_path
 *   and then tell the server to save the current config from the store
 */
function handleDeleteCustomised() {
    // @Todo: Confirm delete!
    const url = store.getAssetDeleteUrl(jsonPath.value);

    // If the image is "empty" just remove it from the list
    if (value.value === "") {
        emits('deleted', jsonPath.value);
        return;
    }

    api.client.delete(url).then(response => {
        if (response.data.original_path) {
            // Overwrite Image Path with original path
            value.value = response.data.original_path;
            // Step-Editoren persistieren erst über "Speichern und schließen"
            // beziehungsweise die explizite Entscheidung beim App-Wechsel.
            persistEditorFieldChange(props.storeName, () => store.save());
        } else {
            // There is no original, which means we are probably inside a repeater field.
            // Inform ImageRepeaterField via event (it will remove this ImageField from the list)
            emits('deleted', jsonPath.value);
        }
    });
}

watch(fileUrl, getFileInfo);

function getFileInfo() {
    api.client.get(route('assets.getFileInfo', {path: fileUrl.value}))
        .then(response => {
            fileInfo.value = response.data;
        });
}

onMounted(() => {
    if (fileUrl.value) {
        getFileInfo();
    }
});
</script>

<style scoped>

</style>
